import { CartLinesUpdateEvent } from '@shopify/events';

class CompleteYourRide extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cards = [...this.querySelectorAll('[data-ck-ride-card]')];
    this.statusElement = this.querySelector('[data-ck-ride-status]');
    this.addButton = this.querySelector('[data-ck-ride-add]');
    this.buttonLabel = this.querySelector('[data-ck-ride-button-label]');
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'AUD';
    this.busy = false;

    this.hideFallbackRecommendations();

    this.cards.forEach((card) => {
      const checkbox = card.querySelector('[data-ck-ride-check]');
      const variantSelect = card.querySelector('[data-ck-ride-variant]');

      if (variantSelect && checkbox) {
        checkbox.checked = false;
      }

      checkbox?.addEventListener('change', () => this.update());

      variantSelect?.addEventListener('change', (event) => {
        const select = event.currentTarget;
        this.updateCardPrice(card, select);

        if (checkbox) {
          checkbox.checked = Boolean(select.value);
        }

        this.update();
      });
    });

    this.addButton?.addEventListener('click', () => this.addSelected());
    this.update();
  }

  hideFallbackRecommendations() {
    document.querySelectorAll('product-recommendations.product-recommendations').forEach((recommendations) => {
      recommendations.hidden = true;
      recommendations.closest('.shopify-section')?.setAttribute('data-ck-related-hidden', 'true');
    });
  }

  updateCardPrice(card, select) {
    const price = card.querySelector('[data-ck-ride-price]');
    if (!price) return;

    const option = select.selectedOptions[0];
    price.textContent = option?.value ? option.dataset.money : price.dataset.defaultMoney;
  }

  getState() {
    const items = [];
    let selectedCount = 0;
    let unresolved = false;
    let totalCents = 0;

    this.cards.forEach((card) => {
      const checkbox = card.querySelector('[data-ck-ride-check]');
      const selected = Boolean(checkbox?.checked);
      card.classList.toggle('is-selected', selected);

      if (!selected) return;

      selectedCount += 1;
      const select = card.querySelector('[data-ck-ride-variant]');

      if (select) {
        const option = select.selectedOptions[0];
        if (!option?.value) {
          unresolved = true;
          return;
        }

        totalCents += Number(option.dataset.price || 0);
        items.push({ id: Number(option.value), quantity: 1 });
        return;
      }

      const variantId = card.dataset.variantId;
      if (!variantId) {
        unresolved = true;
        return;
      }

      totalCents += Number(card.dataset.price || 0);
      items.push({ id: Number(variantId), quantity: 1 });
    });

    return { items, selectedCount, unresolved, totalCents };
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  getButtonLabel(state) {
    if (this.busy) return 'ADDING…';
    if (state.selectedCount === 0) return 'SELECT AN EXTRA';
    if (state.unresolved) return 'CHOOSE OPTIONS TO CONTINUE';

    const noun = state.selectedCount === 1 ? 'EXTRA' : 'EXTRAS';
    return `ADD ${state.selectedCount} ${noun} — ${this.formatMoney(state.totalCents)} →`;
  }

  update(preserveStatus = false) {
    const state = this.getState();

    if (this.buttonLabel) {
      this.buttonLabel.textContent = this.getButtonLabel(state);
    }

    if (this.statusElement && !preserveStatus) {
      this.statusElement.textContent = '';
    }

    if (this.addButton) {
      this.addButton.disabled = this.busy || state.selectedCount === 0 || state.unresolved;
    }

    return state;
  }

  async refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  async addSelected() {
    const state = this.update();
    if (this.busy || !state.items.length || state.unresolved) return;

    this.busy = true;
    this.update(true);
    this.statusElement.textContent = 'Adding your extras to the cart…';

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    let eventSettled = false;

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: state.items.map((item) => ({
          merchandiseId: item.id.toString(),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: state.items,
          sections: cartItemComponentsSectionIds.join(','),
        }),
      });

      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        const cart = await this.refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'complete-your-ride',
            sourceId: this.id || 'ck-complete-your-ride',
            itemCount: state.items.length,
            productId: this.dataset.productId,
            sections: addData.sections,
          },
        });
        eventSettled = true;

        throw new Error(addData.description || addData.message || 'Unable to add the selected products.');
      }

      const cart = await this.refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'complete-your-ride',
          sourceId: this.id || 'ck-complete-your-ride',
          itemCount: state.items.length,
          productId: this.dataset.productId,
          sections: addData.sections,
          didError: false,
        },
      });
      eventSettled = true;

      this.statusElement.textContent = `Added ${state.items.length} ${state.items.length === 1 ? 'extra' : 'extras'} to your cart.`;
    } catch (error) {
      if (!eventSettled) {
        deferredEventPromise.reject(error);
        eventSettled = true;
      }

      this.statusElement.textContent = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
    } finally {
      this.busy = false;
      this.update(true);
    }
  }
}

if (!customElements.get('complete-your-ride')) {
  customElements.define('complete-your-ride', CompleteYourRide);
}
